@if (isOpen$ | async) {
  <!-- Modal Backdrop -->
  <div
    class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm animate-fade-in"
    (click)="close()"
  >
    <!-- Modal Content -->
    <div
      class="relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-modal-slide"
      (click)="$event.stopPropagation()"
    >
      @if ((currentView$ | async) === 'info') {
        <!-- INFO VIEW -->
        <!-- Header with gradient -->
        <div class="bg-gradient-to-r from-brand via-orange-500 to-amber-500 p-6 text-white">
          <button
            (click)="close()"
            class="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors cursor-pointer"
          >
            <lucide-icon [img]="icons.X" class="w-4 h-4 text-white"></lucide-icon>
          </button>

          <div class="flex items-center gap-4">
            <div class="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center">
              <lucide-icon [img]="icons.Crown" class="w-7 h-7 text-white"></lucide-icon>
            </div>
            <div>
              <h2 class="text-xl font-bold">Your Subscription</h2>
              <p class="text-white/80 text-sm">Manage your premium plan</p>
            </div>
          </div>
        </div>

        <!-- Content -->
        @if (subscription$ | async; as sub) {
          <div class="p-6">
            <!-- Plan Info Card -->
            <div class="bg-gray-50 rounded-xl p-4 mb-5">
              <div class="flex items-center justify-between mb-4">
                <div>
                  <p class="text-sm text-gray-500 mb-1">Current Plan</p>
                  <p class="text-lg font-bold text-gray-900">{{ getPlanDisplayName(sub.plan) }}</p>
                </div>
                <span
                  class="px-3 py-1 rounded-full text-sm font-semibold"
                  [ngClass]="getStatusColorClasses(sub.status)"
                >
                  {{ getStatusLabel(sub.status) }}
                </span>
              </div>

              <!-- Subscription Details -->
              <div class="space-y-3 pt-3 border-t border-gray-200">
                @if (sub.isTrial && sub.trialEndsAt) {
                  <div class="flex items-center gap-3 text-sm">
                    <lucide-icon [img]="icons.Calendar" class="w-4 h-4 text-gray-400"></lucide-icon>
                    <span class="text-gray-600">Trial ends:</span>
                    <span class="font-medium text-gray-900">{{ formatDate(sub.trialEndsAt) }}</span>
                  </div>
                } @else if (sub.currentPeriodEnd) {
                  <div class="flex items-center gap-3 text-sm">
                    <lucide-icon [img]="icons.Calendar" class="w-4 h-4 text-gray-400"></lucide-icon>
                    <span class="text-gray-600">{{ sub.cancelAtPeriodEnd ? 'Access until:' : 'Renews on:' }}</span>
                    <span class="font-medium text-gray-900">{{ formatDate(sub.currentPeriodEnd) }}</span>
                  </div>
                }

                @if (sub.cancelAtPeriodEnd) {
                  <div class="flex items-center gap-2 text-sm text-amber-600 bg-amber-50 px-3 py-2 rounded-lg">
                    <lucide-icon [img]="icons.AlertCircle" class="w-4 h-4"></lucide-icon>
                    <span>Your subscription will not renew</span>
                  </div>
                }
              </div>
            </div>

            <!-- Features Included -->
            <div class="mb-6">
              <h3 class="text-sm font-semibold text-gray-700 mb-3">What's included:</h3>
              <ul class="space-y-2">
                @for (feature of features; track feature) {
                  <li class="flex items-center gap-2 text-sm text-gray-600">
                    <lucide-icon [img]="icons.CheckCircle" class="w-4 h-4 text-green-500"></lucide-icon>
                    <span>{{ feature }}</span>
                  </li>
                }
              </ul>
            </div>

            <!-- Actions -->
            <div class="space-y-3">
              <button
                (click)="close()"
                class="w-full py-3 px-4 bg-brand hover:bg-brand-dark text-white font-semibold rounded-xl transition-colors cursor-pointer"
              >
                Done
              </button>

              @if (!sub.cancelAtPeriodEnd && sub.status !== 'canceled') {
                <button
                  (click)="showConfirmation()"
                  class="w-full py-3 px-4 bg-white border border-gray-200 text-gray-600 hover:text-red-600 hover:border-red-200 hover:bg-red-50 font-medium rounded-xl transition-all cursor-pointer"
                >
                  Cancel Subscription
                </button>
              }
            </div>

            <!-- Help Text -->
            <p class="mt-4 text-xs text-gray-400 text-center">
              Need help? Contact us at <a href="mailto:justteachsupport@justteach.ai" class="text-brand hover:underline">justteachsupport@justteach.ai</a>
            </p>
          </div>
        } @else {
          <!-- Fallback if no subscription data -->
          <div class="p-6 text-center">
            <p class="text-gray-500">No subscription information available.</p>
            <button
              (click)="close()"
              class="mt-4 py-2 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors cursor-pointer"
            >
              Close
            </button>
          </div>
        }
      } @else if ((currentView$ | async) === 'confirm') {
        <!-- CONFIRMATION VIEW -->
        <!-- Header with warning gradient -->
        <div class="bg-gradient-to-r from-red-500 via-red-600 to-orange-500 p-6 text-white">
          <button
            (click)="goBackToInfo()"
            [disabled]="isCancelling$ | async"
            class="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors cursor-pointer disabled:opacity-50"
          >
            <lucide-icon [img]="icons.X" class="w-4 h-4 text-white"></lucide-icon>
          </button>

          <div class="flex items-center gap-4">
            <div class="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center">
              <lucide-icon [img]="icons.Heart" class="w-7 h-7 text-white"></lucide-icon>
            </div>
            <div>
              <h2 class="text-xl font-bold">We'd hate to see you go!</h2>
              <p class="text-white/80 text-sm">Are you sure you want to cancel?</p>
            </div>
          </div>
        </div>

        <div class="p-6">
          <!-- What you'll lose -->
          <div class="bg-red-50 border border-red-100 rounded-xl p-4 mb-5">
            <h3 class="text-sm font-semibold text-red-800 mb-3 flex items-center gap-2">
              <lucide-icon [img]="icons.AlertCircle" class="w-4 h-4"></lucide-icon>
              You'll lose access to:
            </h3>
            <ul class="space-y-2">
              @for (feature of loseFeatures; track feature) {
                <li class="flex items-center gap-2 text-sm text-red-700">
                  <lucide-icon [img]="icons.XCircle" class="w-4 h-4 text-red-400"></lucide-icon>
                  <span>{{ feature }}</span>
                </li>
              }
            </ul>
          </div>

          <!-- Encouragement message -->
          <div class="bg-amber-50 border border-amber-100 rounded-xl p-4 mb-6">
            <div class="flex gap-3">
              <lucide-icon [img]="icons.Sparkles" class="w-5 h-5 text-amber-500 shrink-0 mt-0.5"></lucide-icon>
              <div>
                <p class="text-sm text-amber-800 font-medium mb-1">Your lesson plans are waiting!</p>
                <p class="text-xs text-amber-700">Keep your premium access and continue saving hours on lesson preparation with AI-powered tools.</p>
              </div>
            </div>
          </div>

          <!-- Actions -->
          <div class="space-y-3">
            <button
              (click)="goBackToInfo()"
              [disabled]="isCancelling$ | async"
              class="w-full py-3 px-4 bg-brand hover:bg-brand-dark text-white font-semibold rounded-xl transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <lucide-icon [img]="icons.Heart" class="w-4 h-4"></lucide-icon>
              Keep My Subscription
            </button>

            <button
              (click)="onConfirmCancel()"
              [disabled]="isCancelling$ | async"
              class="w-full py-3 px-4 bg-white border border-gray-200 text-gray-500 hover:text-red-600 hover:border-red-200 hover:bg-red-50 font-medium rounded-xl transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              @if (isCancelling$ | async) {
                <lucide-icon [img]="icons.Loader2" class="w-4 h-4 animate-spin"></lucide-icon>
                <span>Cancelling...</span>
              } @else {
                <span>Yes, Cancel My Subscription</span>
              }
            </button>
          </div>

          <!-- Help Text -->
          <p class="mt-4 text-xs text-gray-400 text-center">
            Having issues? <a href="mailto:justteachsupport@justteach.ai" class="text-brand hover:underline">Contact support</a> — we're here to help!
          </p>
        </div>
      } @else {
        <!-- VERIFYING VIEW -->
        <!-- Header with processing gradient -->
        <div class="bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 p-6 text-white">
          <div class="flex items-center gap-4">
            <div class="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center">
              <lucide-icon [img]="icons.Loader2" class="w-7 h-7 text-white animate-spin"></lucide-icon>
            </div>
            <div>
              <h2 class="text-xl font-bold">Processing Cancellation</h2>
              <p class="text-white/80 text-sm">Please wait while we confirm...</p>
            </div>
          </div>
        </div>

        <div class="p-6">
          <!-- Progress Bar -->
          <div class="mb-6">
            <div class="flex justify-between items-center mb-2">
              <span class="text-sm font-medium text-gray-700">Updating subscription status</span>
              <span class="text-sm font-medium text-gray-500">{{ progress$ | async | number:'1.0-0' }}%</span>
            </div>
            <div class="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
              <div
                class="h-full bg-gradient-to-r from-amber-500 to-orange-500 rounded-full transition-all duration-150 ease-out"
                [style.width.%]="progress$ | async"
              ></div>
            </div>
          </div>

          <!-- Info message -->
          <div class="bg-amber-50 border border-amber-100 rounded-xl p-4">
            <div class="flex gap-3">
              <lucide-icon [img]="icons.AlertCircle" class="w-5 h-5 text-amber-500 shrink-0 mt-0.5"></lucide-icon>
              <div>
                <p class="text-sm text-amber-800 font-medium mb-1">Almost done!</p>
                <p class="text-xs text-amber-700">We're confirming your cancellation. This usually takes just a moment.</p>
              </div>
            </div>
          </div>
        </div>
      }
    </div>
  </div>
}

